/**
 * RUN 93 — taking the browser's own furniture off a served hearing document, and naming the
 * file the Technical Delegate saves.
 *
 * The TD printed a Hearing Letter on 22 September 2026. The document itself came out right —
 * RUN 88 fixed the letterhead, RUN 91 shaped the Decision Letter — but the sheet he handed
 * across the table also carried, in the browser's own type:
 *
 *     22/09/2026, 09:58 — TD Control Centre And UM Feedback App
 *     https://hockeytd.org/hearings — 1/1
 *
 * On a document that names a player, a charge and a sanction, and is served on a Team Manager,
 * that is three separate lies of context: a wall-clock stamp that is not the time of anything in
 * the case, the name of an internal application, and a URL nobody outside the tournament office
 * can open. And when he saved it as a PDF instead, the file was called
 * `TD Control Centre And UM Feedback App.pdf` — the same for all three documents of every case
 * on the system, which is unfileable.
 *
 * Neither is a bug in the document. Both are the browser drawing over it, and both are fixed
 * from here.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 1. THE HEADER AND FOOTER — `@page { margin: 0 }`, AND THE MARGINS MOVE ONTO THE SHEET
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Chrome, Edge and Safari draw the date, the title, the URL and the page count INSIDE the
 * `@page` margin — that empty band is the only place they have to put them. There is a
 * checkbox for it in the dialogue, it defaults to on, and it is per-installation: a TD at a
 * tournament, on a borrowed laptop, at the moment a hearing finishes, is not going to find it.
 * The only suppression this code can rely on is to leave the engine nowhere to draw: with the
 * page margin at zero there is no margin box, and the header and footer are not rendered.
 *
 * So the 18mm/16mm the stylesheet has always asked `@page` for moves onto the printed sheet as
 * padding on `.printdoc`, and the text lands exactly where it landed yesterday. That is the
 * whole of the visible change — the letter is not re-laid out, nothing is re-typed, and nothing
 * moves by a millimetre. Only the two bands of browser type go away.
 *
 * **WHY PAGE 2 STILL HAS A TOP MARGIN, WHICH IS THE PART THAT NEARLY SANK THIS.** §12's
 * Format-A Decision Letter runs to a second sheet. Padding on a block is applied at the start of
 * its FIRST fragment and the end of its LAST one, so a padded root with `@page { margin: 0 }`
 * gives page one a top margin, the final page a bottom margin, and page two's first line the
 * very top edge of the paper. That would have been a worse document than the one being fixed.
 * `box-decoration-break: clone` is the CSS that says to apply the box's own padding, border and
 * background to EVERY fragment instead — and it was measured before it was trusted. A
 * three-page Decision Letter, rendered against this application's own built stylesheet and
 * printed to PDF in headless Chrome, lands its first line at 20.9mm / 23.4mm / 18.4mm from the
 * top of pages 1, 2 and 3 and its text's left edge at 16.0mm on all three — the same three
 * pages, to within a tenth of a millimetre, as the identical document printed under today's
 * `@page { margin: 18mm 16mm }`. (The three tops differ from each other because the letterhead
 * sits on page one and the page-two and page-three breaks fall at different points in a line
 * box; what matters is that each one is unchanged.) With `box-decoration-break: slice` — the
 * default — pages 2 and 3 start at 0.4mm. The declaration is not a nicety here; it is the thing
 * that makes the approach honest, and it is why the padding is not simply dropped onto
 * `.docbody`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 2. WHY THE `@page` IS INJECTED AND NOT WRITTEN INTO `editorial.css`
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * This is RUN 90's mechanism, deliberately reused rather than reinvented; `matrixPrint.ts`
 * argues it at length and the argument is the same one. `editorial.css` carries an unnamed
 * `@page { size: A4 portrait; margin: 18mm 16mm }` that EVERY document in this system prints
 * under — the three hearing documents, the UM debrief sheet, the appointment matrix. A second
 * unnamed `@page` appended to that append-only file would win by coming later and would silently
 * re-margin all of them: the debrief sheet would lose its margins to a rule written for a
 * hearing letter, and nothing on screen would say so. A named page (`page: hearing`) would scope
 * it properly and is not carried by every engine a TD may be sitting in front of. A `<style>`
 * element added before the dialogue and removed after it is scoped by construction, needs
 * nothing of the engine, and cannot outlive the press.
 *
 * The compensating padding is in the stylesheet (RUN 93's section), keyed off a body class this
 * module adds for the same duration — so the geometry is reviewable where all the other print
 * geometry lives, and is inert on every page and every press that is not this one.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 3. THE FILENAME
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `Save as PDF` names the file after `document.title`, and there is no other lever: the dialogue
 * is the browser's, not ours. So the title is set immediately before the press and restored
 * immediately after it. The TD asked for `DC-Hearing-Letter-PAK-M07` — hyphens, no date — and
 * gave that pattern for batch 1 only. {@link hearingPrintFilename} extends it to the other two
 * (`DC-Hearing-Minutes-…`, `DC-Hearing-Decision-…`) rather than leaving them on the application
 * name, because the three documents of one case are filed together: a folder holding
 * `DC-Hearing-Letter-PAK-M07` beside two files both called after the application would defeat
 * the naming he asked for at the first case that needed a minute and a decision. Extending a
 * stated pattern is a smaller liberty than inventing one, and than doing nothing.
 *
 * No date, as specified — and it is the right call for a document that may be reprinted: a
 * reprint of the same letter is the same document and should not arrive as a second file.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 4. WHAT THIS DOES NOT COVER, SAID PLAINLY
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * A TD who presses ⌘P with the document on screen gets the old sheet: the browser's header and
 * footer, and the application's name on the file. Nothing in a web page can dress a press it is
 * not told about — there is no event before the keyboard shortcut opens the dialogue, and
 * `beforeprint` fires too late to change the page box in any engine here. The on-page `Print`
 * button is the path, it is already the obvious one (it sits alone under an issued document,
 * beside the note saying the browser's dialogue is how a PDF is saved), and this run does not
 * add a second way to print that would need the same dressing.
 *
 * Nothing here runs on screen and nothing it touches survives the press: the style element, the
 * body class and the document title are all put back by one idempotent teardown.
 */

import type { HearingBatch } from "@hockeytd/shared";

/* The one press this system has for putting a document on paper, imported rather than copied —
   the same reason RUN 90 gives: there must be exactly one of it. */
import { printDocument } from "./documentText.js";

/** On `body` for the length of the press; RUN 93's compensating padding hangs off it. */
export const HEARING_PRINT_BODY_CLASS = "is-hearing-print";

/**
 * How long the teardown waits for `afterprint` before doing it anyway.
 *
 * RUN 90's constant and RUN 90's reasoning: `window.print()` blocks until the dialogue is
 * dismissed in every engine this runs in, so the timer is only armed once the press is over and
 * can never strip the dress out from under a print in progress. It is here for the engine that
 * never fires `afterprint` at all — where, without it, the tab would keep the document's
 * filename as its title for the rest of the session.
 */
const HEARING_PRINT_FALLBACK_MS = 1500;

/** The noun each batch puts in its filename — §9's own names for the three documents. */
const HEARING_PRINT_NOUN: Readonly<Record<HearingBatch, string>> = {
  1: "Letter",
  2: "Minutes",
  3: "Decision",
};

/** The fields of the case the filename is built from, and the only ones. */
export interface HearingPrintCase {
  /** The IOC code, e.g. `PAK`. Null on a case whose team is not yet named. */
  teamCode: string | null;
  /** The display match number, e.g. `M07` or `W12`. Null on a non-match incident. */
  matchRef: string | null;
}

/**
 * One token of the filename, reduced to what is safe in a filename on every platform a TD's
 * laptop might be running.
 *
 * Runs of anything that is not a letter or a digit become a single hyphen, which is the
 * separator the TD's pattern already uses, and a token left with nothing is dropped by the
 * caller. Nothing is lower-cased or expanded: `PAK` and `M07` are how the case record spells
 * them and how the TD reads them off a team sheet.
 */
function hearingPrintToken(value: string | null): string | null {
  if (value === null) return null;
  const safe = value
    .replace(/[^A-Za-z0-9]+/gu, "-")
    .replace(/^-+/u, "")
    .replace(/-+$/u, "");
  return safe === "" ? null : safe;
}

/**
 * The name the saved PDF takes — `DC-Hearing-Letter-PAK-M07`, and its honest degradations.
 *
 * The two case fields are optional in the record and so are optional here. A case with no match
 * (a non-match incident, which §14 admits) yields `DC-Hearing-Letter-PAK`; a case with no team
 * named yet yields `DC-Hearing-Letter`. **Neither ever reaches the filename as `null` or
 * `undefined`** — a file called `DC-Hearing-Letter-undefined-M07` would be the kind of defect
 * that outlives the tournament, because it arrives in the TD's filing rather than on his screen.
 *
 * A match with no team keeps the match: `DC-Hearing-Letter-M07`. The TD's pattern does not cover
 * that case, and dropping the match as well would name two different cases' letters identically,
 * which is the one outcome the naming exists to prevent. `M07` and `PAK` are not confusable —
 * one is a match number and the other an IOC code — so the shorter name stays unambiguous.
 *
 * No extension is added. The browser appends `.pdf` itself, and a title ending in `.pdf` is
 * saved by some engines as `….pdf.pdf`.
 */
export function hearingPrintFilename(batch: HearingBatch, kase: HearingPrintCase | null): string {
  const parts = [
    HEARING_PRINT_NOUN[batch],
    hearingPrintToken(kase?.teamCode ?? null),
    hearingPrintToken(kase?.matchRef ?? null),
  ].filter((part): part is string => part !== null);
  return `DC-Hearing-${parts.join("-")}`;
}

/**
 * Print one hearing document: name the file, take the page margin away so the browser has
 * nowhere to draw its header and footer, open the dialogue, and put all three back.
 *
 * `kase` is the case the workspace is already holding, so what the file is called can never
 * disagree with what the document says. A null case is accepted rather than refused: the press
 * is the TD's output path and a document must print even where the record is thin, just with a
 * shorter name.
 */
export function printHearingDocument(
  batch: HearingBatch,
  kase: HearingPrintCase | null,
): void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    printDocument();
    return;
  }

  const restoreTitle = document.title;
  document.title = hearingPrintFilename(batch, kase);
  document.body.classList.add(HEARING_PRINT_BODY_CLASS);

  const page = document.createElement("style");
  page.dataset.hearingPrint = "";
  /* Portrait is restated rather than inherited: this element REPLACES the stylesheet's unnamed
     `@page` for the length of the press (later in the cascade, same null specificity), so
     anything it leaves out is left at the engine's default rather than at the stylesheet's. */
  page.textContent = "@page { size: A4 portrait; margin: 0; }";
  document.head.append(page);

  let timer = 0;
  let torn = false;
  const teardown = (): void => {
    if (torn) return;
    torn = true;
    window.removeEventListener("afterprint", teardown);
    window.clearTimeout(timer);
    page.remove();
    document.body.classList.remove(HEARING_PRINT_BODY_CLASS);
    document.title = restoreTitle;
  };

  window.addEventListener("afterprint", teardown);
  printDocument();
  if (!torn) timer = window.setTimeout(teardown, HEARING_PRINT_FALLBACK_MS);
}
